"use client"

import * as React from "react"
import { Delete } from "lucide-react"

interface CalculatorWidgetProps {
  calcFormula: string
  setCalcFormula: React.Dispatch<React.SetStateAction<string>>
  calcDisplay: string
  setCalcDisplay: React.Dispatch<React.SetStateAction<string>>
  showCalcHistory: boolean
  setShowCalcHistory: React.Dispatch<React.SetStateAction<boolean>>
  calcHistory: Array<{ expr: string; result: string }>
  setCalcHistory: React.Dispatch<React.SetStateAction<Array<{ expr: string; result: string }>>>
  setCalcResetOnNext: React.Dispatch<React.SetStateAction<boolean>>
  handleCalcPress: (val: string) => void
}

export function CalculatorWidget({
  calcFormula,
  setCalcFormula,
  calcDisplay,
  setCalcDisplay,
  showCalcHistory,
  setShowCalcHistory,
  calcHistory,
  setCalcHistory,
  setCalcResetOnNext,
  handleCalcPress,
}: CalculatorWidgetProps) {
  return (
    <div className="flex flex-col h-full bg-white dark:bg-zinc-900 text-zinc-900 dark:text-zinc-100 select-none">
      <div className="flex flex-col justify-end items-end px-5 py-4 bg-zinc-50 dark:bg-zinc-950/60 border-b border-zinc-100 dark:border-zinc-800/40 shrink-0 select-text">
        <span className="text-[11px] text-zinc-450 dark:text-zinc-500 font-mono min-h-[16px] truncate w-full text-right tracking-wider select-none">
          {calcFormula || "\u00a0"}
        </span>
        <span className="text-2xl font-bold font-sans text-zinc-850 dark:text-white truncate tracking-tight mt-1">
          {calcDisplay}
        </span>
      </div>

      <div className="flex gap-1 p-2.5 bg-zinc-50/50 dark:bg-zinc-900/50 border-b border-zinc-100 dark:border-zinc-800/40 shrink-0">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            setShowCalcHistory(false)
          }}
          className={`flex-1 text-[11px] font-bold rounded-lg py-1.5 transition-colors cursor-pointer ${!showCalcHistory
              ? "bg-[#004EB1] text-white shadow-xs"
              : "bg-zinc-100 dark:bg-zinc-800 text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300"
            }`}
        >
          Teclado
        </button>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            setShowCalcHistory(true)
          }}
          className={`flex-1 text-[11px] font-bold rounded-lg py-1.5 transition-colors cursor-pointer ${showCalcHistory
              ? "bg-[#004EB1] text-white shadow-xs"
              : "bg-zinc-100 dark:bg-zinc-800 text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300"
            }`}
        >
          Histórico {calcHistory.length > 0 && `(${calcHistory.length})`}
        </button>
      </div>

      {showCalcHistory ? (
        <div className="flex flex-col gap-2 p-3 flex-1 overflow-y-auto">
          {calcHistory.length === 0 ? (
            <div className="text-center text-xs text-zinc-400 dark:text-zinc-600 py-10 font-sans">
              Nenhum cálculo registrado ainda.
            </div>
          ) : (
            <>
              <div className="flex justify-end shrink-0 mb-1">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    setCalcHistory([])
                  }}
                  className="text-[10px] text-red-500 hover:text-red-600 font-bold font-sans px-2.5 py-1 rounded hover:bg-red-50 dark:hover:bg-red-950/20 transition-colors cursor-pointer"
                >
                  Limpar Histórico
                </button>
              </div>
              <div className="flex flex-col gap-1.5 overflow-y-auto pr-1">
                {calcHistory.map((h, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => {
                      setCalcDisplay(h.result)
                      setCalcFormula("")
                      setCalcResetOnNext(true)
                    }}
                    className="flex flex-col items-end px-3 py-2 rounded-xl bg-zinc-100/60 dark:bg-zinc-800/40 hover:bg-zinc-200/50 dark:hover:bg-zinc-800/60 transition-colors cursor-pointer text-right w-full border border-zinc-200/30 dark:border-zinc-700/30 shrink-0"
                  >
                    <span className="text-[10px] text-zinc-450 dark:text-zinc-500 font-mono">{h.expr}</span>
                    <span className="text-sm font-bold text-zinc-850 dark:text-white font-mono">= {h.result}</span>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-4 gap-2 p-3 bg-white dark:bg-zinc-900 justify-items-center flex-1 items-center overflow-y-auto scrollbar-none">
          {[
            { label: "AC", val: "AC", type: "fn" },
            { label: "±", val: "±", type: "fn" },
            { label: "%", val: "%", type: "fn" },
            { label: "÷", val: "/", type: "op" },

            { label: "7", val: "7", type: "num" },
            { label: "8", val: "8", type: "num" },
            { label: "9", val: "9", type: "num" },
            { label: "×", val: "*", type: "op" },

            { label: "4", val: "4", type: "num" },
            { label: "5", val: "5", type: "num" },
            { label: "6", val: "6", type: "num" },
            { label: "−", val: "-", type: "op" },

            { label: "1", val: "1", type: "num" },
            { label: "2", val: "2", type: "num" },
            { label: "3", val: "3", type: "num" },
            { label: "+", val: "+", type: "op" },

            { label: "0", val: "0", type: "num", colSpan: 2 },
            { label: ",", val: ".", type: "num" },
            { label: "=", val: "=", type: "op" },
          ].map((item, idx) => {
            const isOperator = item.type === "op"
            const isFn = item.type === "fn"

            return (
              <button
                key={idx}
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  handleCalcPress(item.val)
                }}
                className={`h-11 rounded-xl flex items-center justify-center font-bold text-sm transition-all active:scale-95 cursor-pointer shadow-xs ${item.colSpan === 2 ? "col-span-2 w-full text-left pl-5 justify-start" : "w-full"
                  } ${isOperator
                    ? "bg-[#004EB1] hover:bg-[#003d8d] text-white"
                    : isFn
                      ? "bg-zinc-200/70 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-200 hover:bg-zinc-300 dark:hover:bg-zinc-700"
                      : "bg-zinc-100 dark:bg-zinc-800/50 text-zinc-900 dark:text-zinc-100 hover:bg-zinc-200 dark:hover:bg-zinc-700 border border-zinc-200/40 dark:border-zinc-800/40"
                  }`}
              >
                {item.label}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
